import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import FileSaver from 'file-saver';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { forkJoin } from 'rxjs';
import { firstValueFrom } from 'rxjs';

import { PicklistApiService } from '../../api';
import { LoadingDialogUtilService } from '../../util';

import { FileTypeConstant } from './../../../../../../app/core/_constant/index.constants';
import { FeatureFlagUtilService } from './../../util/feature-flag-util.service';

@Injectable({
  providedIn: 'root',
})
export class PackingFacadeService {
  constructor(
    private dialog: MatDialog,
    private picklistService: PicklistApiService,
    private loadingDialogService: LoadingDialogUtilService,
    private featureFlagUtilService: FeatureFlagUtilService,
  ) {}

  async exportPackingSlipForOrdersToPdf(salesOrderIds: number[]): Promise<void> {
    const BATCH_SIZE = 15;
    const batches = [];
    for (let i = 0; i < salesOrderIds.length; i += BATCH_SIZE) {
      batches.push(salesOrderIds.slice(i, i + BATCH_SIZE));
    }

    this.loadingDialogService.show('Creating PDF, please wait...');

    try {
      // Create all batch observables
      const batchRequests = batches.map((batch) => {
        const params = {
          exportType: FileTypeConstant.PDF,
          salesOrderIds: batch.join(','),
        };
        return this.picklistService.exportBulkPackingSlip(params);
      });

      // Run all requests in parallel and wait for all to finish
      const pdfBlobs: Blob[] = await firstValueFrom(forkJoin(batchRequests));

      // Merge PDFs using pdf-lib
      const mergedPdf = await PDFDocument.create();
      for (const blob of pdfBlobs) {
        const arrayBuffer = await blob.arrayBuffer();
        const pdf = await PDFDocument.load(arrayBuffer);
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach((page) => mergedPdf.addPage(page));
      }

      // Add page footers
      const totalPages = mergedPdf.getPageCount();
      const font = await mergedPdf.embedFont(StandardFonts.Helvetica);
      const fontSize = 8;
      const textColor = rgb(0.6, 0.6, 0.6); // lighter gray
      for (let i = 0; i < totalPages; i++) {
        const page = mergedPdf.getPage(i);
        const { width } = page.getSize();
        const footerText = `Page ${i + 1} / ${totalPages}`;
        const textWidth = font.widthOfTextAtSize(footerText, fontSize);
        const x = width - textWidth - 30; // 30 units from the right edge
        const y = 20; // 20 units from the bottom
        page.drawText(footerText, {
          x,
          y,
          size: fontSize,
          font,
          color: textColor,
        });
      }

      const mergedPdfBytes = await mergedPdf.save();
      const mergedBlob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
      FileSaver.saveAs(mergedBlob, 'Packing Slips.pdf');
      this.loadingDialogService.close();
    } catch (error) {
      this.loadingDialogService.close();
      this.loadingDialogService.showDialogMessage('Error Export');
    }
  }

  get ftTemplateManagement(): boolean {
    return this.featureFlagUtilService.isFeatureEnabled(
      'red_DV-3138_template_management',
    );
  }
}
